"use client"
import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

type Variant = 'default' | 'cream' | 'pink' | 'orange';
type Size = 'sm' | 'md' | 'lg';
type ContainerSize = 'sm' | 'md' | 'lg';
type WaveTo = 'white' | 'cream';

interface SectionProps {
  children: React.ReactNode;
  className?: string;
  variant?: Variant;
  size?: Size;
  containerSize?: ContainerSize;
  withWave?: boolean;
  waveTo?: WaveTo;
  withBackground?: boolean;
  animate?: boolean;
  id?: string;
}

const variants: Record<Variant, string> = {
  default: 'bg-background text-foreground',
  cream: 'bg-mochi-cream-50 text-neutral-800',
  pink: 'bg-mochi-pink-50 text-neutral-800',
  orange: 'bg-mochi-orange-50 text-neutral-800',
};

const sizes: Record<Size, string> = {
  sm: 'py-12 md:py-16',
  md: 'py-16 md:py-24',
  lg: 'py-20 md:py-32',
};

const containerSizes: Record<ContainerSize, string> = {
  sm: 'max-w-5xl',
  md: 'max-w-6xl',
  lg: 'max-w-7xl',
};

const Section: React.FC<SectionProps> = ({
  children,
  className,
  variant = 'default',
  size = 'md',
  containerSize = 'md',
  withWave = false,
  waveTo = 'white',
  withBackground = true,
  animate = true,
  id,
}) => {
  const sectionClasses = cn(
    // Base styles
    'relative w-full overflow-hidden',
    // Consistent spacing
    'px-4 sm:px-6 md:px-8',
    sizes[size],
    // Variant styles
    withBackground && variants[variant],
    className
  );

  const containerClasses = cn(
    // Base container styles
    'mx-auto w-full',
    containerSizes[containerSize],
    // Grid system
    'grid grid-cols-1 gap-y-8 md:gap-y-12'
  );

  const content = (
    <div className={containerClasses}>
      {children}
    </div>
  );

  // Wave SVG patterns for section transitions
  const wavePatterns: Record<WaveTo, JSX.Element> = {
    white: (
      <svg className="absolute bottom-0 left-0 right-0 text-white" viewBox="0 0 1440 64" preserveAspectRatio="none">
        <path
          fill="currentColor"
          d="M0,0 C480,64 960,64 1440,0 L1440,64 L0,64 Z"
        />
      </svg>
    ),
    cream: (
      <svg className="absolute bottom-0 left-0 right-0 text-mochi-cream-50" viewBox="0 0 1440 64" preserveAspectRatio="none">
        <path
          fill="currentColor"
          d="M0,0 C480,64 960,64 1440,0 L1440,64 L0,64 Z"
        />
      </svg>
    ),
  };

  return (
    <section id={id} className={sectionClasses}>
      {animate ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: [0.33, 1, 0.68, 1] }}
        >
          {content}
        </motion.div>
      ) : (
        content
      )}
      {withWave && wavePatterns[waveTo]}
    </section>
  );
};

export default Section; 